/**
 * ClinicCasesList — Phase-1 operational case list
 * =================================================
 *
 * TREATMENT-JOURNEY-IMPLEMENTATION-001 · Phase 1 rewrite.
 *
 * This file is the real operational Patient Cases surface for clinic
 * admin and coordinator roles. As of Phase 1 it adopts the shared
 * journey-UX primitives (`DataGrid`, `FilterChips`, `BulkBar`,
 * `DetailDrawer`, `Pill`) from `components/journey-ux/`.
 *
 *   - No new API endpoints
 *   - No new collections
 *   - No RBAC change
 *   - No DB migration
 *   - All existing data-testids preserved (cases-list-*) so prior
 *     regression suites keep working
 *
 * Reuses (canonical, untouched):
 *   - GET /api/cases                       (clinic-scoped, JWT-bound)
 *   - GET /api/milestone-tasks/case/:id    (per-case task summary)
 *
 * UI terminology cleanup applied in this revision (per
 * TERMINOLOGY_MAP.md):
 *   - "Open workflow"             → "Open case"
 *   - Section label "Treatment cases" preserved (already approved)
 *   - Phase-1 footnote references rephrased to drop "milestone"/"workflow"
 *
 * Reachable from:
 *   - provider_admin    → /clinic/admin    → nav tab "Cases"
 *   - provider_coordinator → /clinic/operations → "Cases" action card
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { formatDateLocale as _fmtDate } from '../../utils/dateFormat';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Briefcase,
  ArrowRight,
  Building2,
  RefreshCw,
  AlertCircle,
  Inbox,
} from 'lucide-react';

import { useClinicScope } from '../../hooks/useClinicScope';
import { api } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';

import {
  DataGrid,
  FilterChips,
  BulkBar,
  DetailDrawer,
  DrawerField,
  Pill,
} from '../../components/journey-ux';

// Task statuses we consider "outstanding" (i.e. still requiring work).
// Anything not in this set (done / cancelled / skipped) is considered
// closed for counting purposes.
const OUTSTANDING_TASK_STATUSES = new Set([
  'pending',
  'assigned',
  'in_progress',
  'blocked',
  'scheduled',
]);

// Map raw backend case status → semantic Pill tone (journey-ux palette).
const STATUS_TONE = {
  intake: 'sky',
  in_progress: 'amber',
  active: 'amber',
  completed: 'emerald',
  closed: 'slate',
  cancelled: 'slate',
};

function shortId(id) {
  if (!id || typeof id !== 'string') return '—';
  if (id.length <= 12) return id;
  return `${id.slice(0, 8)}…${id.slice(-4)}`;
}

function formatDateLocale(iso) {
  if (!iso) return '—';
  try {
    return _fmtDate(iso);
  } catch {
    return '—';
  }
}

export default function ClinicCasesList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { clinicId, userName } = useClinicScope();

  const [cases, setCases] = useState([]);
  const [taskSummaries, setTaskSummaries] = useState({}); // case_id → { total, outstanding }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  // Filter / select / drawer state.
  const [filterId, setFilterId] = useState('all');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [activeCase, setActiveCase] = useState(null);

  // 1. Load the clinic's cases.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const { data } = await api.get('/cases');
        if (cancelled) return;
        const list = Array.isArray(data?.cases) ? data.cases : [];
        setCases(list);
      } catch (e) {
        if (!cancelled) {
          setError(
            e?.response?.data?.message || e.message || 'Failed to load cases'
          );
          setCases([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshTick]);

  // 2. Per-case task summaries (parallel — Phase-1 keeps it simple).
  useEffect(() => {
    if (!cases.length) {
      setTaskSummaries({});
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        cases.map(async (c) => {
          try {
            const { data } = await api.get(`/milestone-tasks/case/${c.case_id}`);
            const tasks = Array.isArray(data?.tasks) ? data.tasks : [];
            const total = tasks.length;
            const outstanding = tasks.filter((t) =>
              OUTSTANDING_TASK_STATUSES.has((t.status || '').toLowerCase())
            ).length;
            return [c.case_id, { total, outstanding }];
          } catch {
            return [c.case_id, { total: 0, outstanding: 0, error: true }];
          }
        })
      );
      if (cancelled) return;
      setTaskSummaries(Object.fromEntries(entries));
    })();
    return () => {
      cancelled = true;
    };
  }, [cases]);

  const aggregate = useMemo(() => {
    let totalTasks = 0;
    let outstandingTasks = 0;
    Object.values(taskSummaries).forEach((s) => {
      totalTasks += s.total || 0;
      outstandingTasks += s.outstanding || 0;
    });
    return {
      cases: cases.length,
      totalTasks,
      outstandingTasks,
    };
  }, [cases, taskSummaries]);

  // Counts for filter chips. "blocked" = cases with outstanding > 0.
  const filterCounts = useMemo(() => {
    let blocked = 0;
    cases.forEach((c) => {
      const s = taskSummaries[c.case_id];
      if (s && s.outstanding > 0) blocked += 1;
    });
    return { all: cases.length, blocked };
  }, [cases, taskSummaries]);

  const filteredCases = useMemo(() => {
    if (filterId === 'blocked') {
      return cases.filter((c) => (taskSummaries[c.case_id]?.outstanding || 0) > 0);
    }
    return cases;
  }, [cases, taskSummaries, filterId]);

  // Selection handlers.
  const toggleRow = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelectedIds((prev) => {
      if (prev.size === filteredCases.length) return new Set();
      return new Set(filteredCases.map((c) => c.case_id));
    });
  }, [filteredCases]);

  // No-op bulk actions for Phase 1 — surfaces exist, behaviour deferred.
  const handleBulkRemind = useCallback(() => {
    // Phase 1: reminders surface intentionally not wired.
    // Action exists in the UI so the testing agent can assert the bar.
  }, []);

  const columns = useMemo(
    () => [
      {
        id: 'case_id',
        header: 'Case',
        width: 'w-28',
        cellClassName: 'font-mono text-slate-900 font-semibold',
        accessor: (row) => shortId(row.case_id),
      },
      {
        id: 'patient',
        header: 'Patient',
        accessor: (row) => (
          <span className="font-mono text-[11px] text-slate-500">
            {shortId(row.person_id)}
          </span>
        ),
      },
      {
        id: 'treatment',
        header: 'Treatment',
        accessor: (row) => row.treatment_name || '—',
      },
      {
        id: 'status',
        header: 'Status',
        width: 'w-28',
        render: (row) => {
          const k = (row.status || '').toLowerCase() || 'unknown';
          return <Pill tone={STATUS_TONE[k] || 'slate'}>{k}</Pill>;
        },
      },
      {
        id: 'tasks',
        header: 'Tasks',
        align: 'right',
        width: 'w-16',
        render: (row) => {
          const s = taskSummaries[row.case_id];
          if (!s) return <span className="text-slate-400">…</span>;
          return <span className="font-mono text-slate-700">{s.total}</span>;
        },
      },
      {
        id: 'outstanding',
        header: 'Pending Items',
        align: 'right',
        width: 'w-24',
        render: (row) => {
          const s = taskSummaries[row.case_id];
          if (!s) return <span className="text-slate-400">…</span>;
          if (s.outstanding === 0)
            return <span className="text-slate-400">0</span>;
          return <Pill tone="rose">{s.outstanding}</Pill>;
        },
      },
      {
        id: 'created',
        header: 'Created',
        width: 'w-28',
        cellClassName: 'text-slate-500',
        accessor: (row) => formatDateLocale(row.created_at),
      },
      {
        id: 'open',
        header: '',
        align: 'right',
        width: 'w-24',
        render: (row) => (
          <Button
            size="sm"
            onClick={() => navigate(`/clinic/cases/${row.case_id}/workflow`)}
            data-testid={`cases-list-row-${row.case_id}-open-workflow`}
            className="bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] h-7 px-2.5"
          >
            Open
            <ArrowRight className="ml-1 h-3 w-3" />
          </Button>
        ),
      },
    ],
    [navigate, taskSummaries]
  );

  if (!clinicId) {
    return (
      <div
        data-testid="cases-list-no-clinic-scope"
        className="max-w-3xl mx-auto p-8 text-center text-gray-600"
      >
        <AlertCircle className="h-10 w-10 text-amber-500 mx-auto mb-3" />
        <h1 className="text-xl font-semibold text-gray-900">
          No clinic scope detected on your account
        </h1>
        <p className="mt-2 text-sm">
          Your role is bound to a clinic in the canonical RBAC scope, but the
          active clinic context could not be resolved. Please sign out and back
          in.
        </p>
      </div>
    );
  }

  const activeSummary = activeCase ? taskSummaries[activeCase.case_id] : null;

  return (
    <div
      data-testid="clinic-cases-list"
      className="min-h-[calc(100vh-4rem)] bg-slate-50"
    >
      {/* Header — kept compact (Phase 1 density). */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-[1400px] mx-auto px-5 py-3 flex items-center gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Badge
                variant="outline"
                className="border-slate-200 text-slate-700 bg-slate-50 text-[10px] h-5"
                data-testid="cases-list-scope-badge"
              >
                Clinic-scoped
              </Badge>
              <h1
                className="text-[16px] font-semibold text-slate-900"
                data-testid="cases-list-title"
              >
                Patient Cases
              </h1>
            </div>
            <div className="mt-0.5 flex items-center gap-2 text-[11px] text-slate-500">
              <Building2 className="h-3 w-3" />
              <span data-testid="cases-list-clinic-id">Clinic {clinicId}</span>
              {userName && (
                <>
                  <span className="text-slate-300">·</span>
                  <span data-testid="cases-list-user-name">
                    Signed in as{' '}
                    <span className="font-medium text-slate-700">{userName}</span>
                  </span>
                </>
              )}
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setRefreshTick((t) => t + 1)}
              data-testid="cases-list-refresh"
              className="h-7 text-[11px]"
            >
              <RefreshCw className="h-3 w-3 mr-1" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Compact KPI strip — replaces the prior 3 large cards (density). */}
      <div className="bg-white border-b border-slate-100">
        <div className="max-w-[1400px] mx-auto px-5 py-2 grid grid-cols-3 gap-3">
          <div data-testid="cases-list-summary-cases" className="flex items-baseline gap-2">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              Active cases
            </span>
            <span className="text-[18px] font-semibold text-slate-900">
              {loading ? '…' : aggregate.cases}
            </span>
          </div>
          <div data-testid="cases-list-summary-tasks" className="flex items-baseline gap-2">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              Total tasks
            </span>
            <span className="text-[18px] font-semibold text-slate-900">
              {loading ? '…' : aggregate.totalTasks}
            </span>
          </div>
          <div data-testid="cases-list-summary-outstanding" className="flex items-baseline gap-2">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              Outstanding
            </span>
            <span
              className={`text-[18px] font-semibold ${
                aggregate.outstandingTasks > 0
                  ? 'text-rose-600'
                  : 'text-slate-900'
              }`}
            >
              {loading ? '…' : aggregate.outstandingTasks}
            </span>
          </div>
        </div>
      </div>

      {/* Filter chips + bulk bar. */}
      <FilterChips
        testIdPrefix="cases-list-filter"
        activeId={filterId}
        onChange={(id) => {
          setFilterId(id);
          setSelectedIds(new Set());
        }}
        chips={[
          { id: 'all', label: 'All', count: filterCounts.all },
          { id: 'blocked', label: 'Pending Items', count: filterCounts.blocked },
        ]}
        resultLabel={
          loading
            ? 'Loading…'
            : `${filteredCases.length} case${filteredCases.length === 1 ? '' : 's'}`
        }
        rightSlot={
          <BulkBar
            testId="cases-list-bulk-bar"
            selectedCount={selectedIds.size}
            actions={[
              { id: 'remind', label: 'Remind', onClick: handleBulkRemind },
              {
                id: 'clear',
                label: 'Clear',
                onClick: () => setSelectedIds(new Set()),
              },
            ]}
          />
        }
      />

      {/* Body: grid + drawer side-by-side. */}
      <div className="flex max-w-[1400px] mx-auto">
        <div className="flex-1 min-w-0">
          {error ? (
            <Card className="m-5">
              <CardContent className="px-6 py-10 text-center text-red-600">
                <AlertCircle className="h-6 w-6 mx-auto mb-2" />
                <span data-testid="cases-list-error">{error}</span>
              </CardContent>
            </Card>
          ) : (
            <DataGrid
              testId="cases-list-table"
              columns={columns}
              rows={filteredCases}
              rowKey={(row) => row.case_id}
              selectedIds={selectedIds}
              onToggleRow={toggleRow}
              onToggleAll={toggleAll}
              onRowClick={setActiveCase}
              activeRowId={activeCase?.case_id}
              loading={loading}
              loadingMessage={
                <span data-testid="cases-list-loading">{t('provider.cases.loading')}</span>
              }
              emptyMessage={
                <span data-testid="cases-list-empty">
                  <Inbox className="h-6 w-6 mx-auto mb-2 text-slate-400 inline-block" />
                  <br />
                  {filterId === 'blocked'
                    ? 'No cases with outstanding tasks.'
                    : 'No active cases at this clinic yet.'}
                </span>
              }
              maxHeightClass="h-[calc(100vh-260px)]"
            />
          )}

          {/* Footer crumb (kept for audit trail; no forbidden terms). */}
          <div className="max-w-[1400px] mx-auto px-5 py-3 flex items-center gap-3 text-[11px] text-slate-400">
            <Link
              to="/clinic/admin"
              className="hover:text-slate-700"
              data-testid="cases-list-back-to-admin"
            >
              ← Back to dashboard
            </Link>
            <span className="text-slate-300">·</span>
            <span>
              Phase-1 case list — patient profile enrichment, assignment, and
              per-role inboxes are deferred to later phases.
            </span>
          </div>
        </div>

        {/* Right-side detail drawer. */}
        <DetailDrawer
          testId="cases-list-drawer"
          open={!!activeCase}
          onClose={() => setActiveCase(null)}
          eyebrow={activeCase ? `Patient Case · ${shortId(activeCase.case_id)}` : ''}
          title={activeCase?.treatment_name || 'Patient Case'}
          footer={
            activeCase && (
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  onClick={() =>
                    navigate(`/clinic/cases/${activeCase.case_id}/workflow`)
                  }
                  data-testid="cases-list-drawer-open-case"
                  className="bg-slate-900 hover:bg-slate-800 text-white text-[11px] h-7 flex-1"
                >
                  Open case
                  <ArrowRight className="ml-1 h-3 w-3" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setActiveCase(null)}
                  data-testid="cases-list-drawer-dismiss"
                  className="text-[11px] h-7"
                >
                  Close
                </Button>
              </div>
            )
          }
        >
          {activeCase && (
            <>
              <DrawerField
                label="Case ID"
                value={
                  <span className="font-mono text-[11px]">
                    {activeCase.case_id}
                  </span>
                }
                testId="cases-list-drawer-case-id"
              />
              <DrawerField
                label="Patient"
                value={
                  <span className="font-mono text-[11px]">
                    {shortId(activeCase.person_id)}
                  </span>
                }
                testId="cases-list-drawer-patient"
              />
              <DrawerField
                label="Treatment"
                value={activeCase.treatment_name || '—'}
                testId="cases-list-drawer-treatment"
              />
              <DrawerField
                label="Status"
                value={
                  <Pill
                    tone={
                      STATUS_TONE[(activeCase.status || '').toLowerCase()] ||
                      'slate'
                    }
                  >
                    {(activeCase.status || 'unknown').toLowerCase()}
                  </Pill>
                }
                testId="cases-list-drawer-status"
              />
              <DrawerField
                label="Created"
                value={formatDateLocale(activeCase.created_at)}
                testId="cases-list-drawer-created"
              />
              <div className="border-t border-slate-100 pt-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
                  Tasks
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-slate-50 rounded p-2">
                    <div className="text-[10px] text-slate-500">Total</div>
                    <div className="text-[16px] font-semibold text-slate-900">
                      {activeSummary?.total ?? '…'}
                    </div>
                  </div>
                  <div className="bg-slate-50 rounded p-2">
                    <div className="text-[10px] text-slate-500">{t('provider.cases.pendingItemsCol')}</div>
                    <div
                      className={`text-[16px] font-semibold ${
                        (activeSummary?.outstanding || 0) > 0
                          ? 'text-rose-600'
                          : 'text-slate-900'
                      }`}
                    >
                      {activeSummary?.outstanding ?? '…'}
                    </div>
                  </div>
                </div>
              </div>
              <div className="border-t border-slate-100 pt-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
                  Notes
                </div>
                <div className="flex items-center gap-2 text-slate-600">
                  <Briefcase className="h-3.5 w-3.5" />
                  <span>
                    Phase-1 drawer surfaces summary only. Detailed Journey Steps,
                    Requirements and Assignments arrive in Phase 2.
                  </span>
                </div>
              </div>
            </>
          )}
        </DetailDrawer>
      </div>
    </div>
  );
}
